import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, FormControl, FormLabel, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, VStack } from '@chakra-ui/react';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Popper from '@material-ui/core/Popper';
import { ChevronDownIcon, CloseIcon } from './AdminIcon';
import AdminState from './AdminState';
import useAdminToast from './useAdminToast';
import Backend from '../../utils/api';
import { useAnimationToggle } from '../../Contexts/AnimationContext';

const credentialFilter = createFilterOptions({ stringify: option => `${option.name} ${option.id} ${option.provider}` });

export default function BadgeRequirementDialog({ user, labId, onClose, onSaved, finalFocusRef }) {
  const [choices, setChoices] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [credential, setCredential] = useState(null);
  const [tool, setTool] = useState(null);
  const [credentialOpen, setCredentialOpen] = useState(false);
  const [toolOpen, setToolOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [saveError, setSaveError] = useState('');
  const pendingRef = useRef(false);
  const credentialRef = useRef(null);
  const dialogRef = useRef(null);
  // Stay inside the focus-trapped dialog, but outside its scrolling body so
  // lower-field options remain visible above the footer on narrow screens.
  const ChoicePopper = useCallback(props => <Popper {...props} container={dialogRef.current} />, []);
  const toast = useAdminToast();
  const { animationsEnabled } = useAnimationToggle();
  useEffect(() => {
    let active = true;
    setLoadError(false);
    setChoices(null);
    Backend.getBadgeRequirementChoices(user.UID).then(response => {
      if (response?.status !== 200 || response.error || !response.credentials || !response.tools) throw new Error('load_failed');
      if (active) setChoices({
        credentials: response.credentials.slice().sort((a, b) => a.name.localeCompare(b.name)),
        tools: response.tools.slice().sort((a, b) => a.name.localeCompare(b.name)),
      });
    }).catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [user.UID, revision]);
  useEffect(() => { if (choices) credentialRef.current?.focus(); }, [choices]);
  const close = () => { if (!pendingRef.current) onClose(); };
  const submit = async event => {
    event.preventDefault();
    if (!choices || !credential || !tool || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setSaveError('');
    try {
      const response = await Backend.createBadgeRequirement(user.UID, credential, tool.id, labId);
      if (response?.status !== 201 || response.error) {
        setSaveError(response?.status === 400 && /already exists/i.test(response.message || response.error || '') ?
          'This credential is already linked to this tool. Choose another credential or tool.' :
          'Could not add this badge requirement. Your selections are still here. Try again.');
        pendingRef.current = false;
        setPending(false);
        return;
      }
      toast({ title: 'Badge requirement added' });
      onSaved();
    } catch {
      setSaveError('Could not add this badge requirement. Your selections are still here. Try again.');
      pendingRef.current = false;
      setPending(false);
    }
  };
  return <Modal isOpen onClose={close} size="lg" isCentered scrollBehavior="inside"
    initialFocusRef={credentialRef} finalFocusRef={finalFocusRef} closeOnEsc={!pending}
    closeOnOverlayClick={!pending} motionPreset={animationsEnabled ? 'scale' : 'none'}>
    <ModalOverlay />
    <ModalContent ref={dialogRef} as="form" onSubmit={submit}>
      <ModalHeader>Add badge requirement</ModalHeader>
      <ModalCloseButton aria-label="Close badge requirement editor" isDisabled={pending}><CloseIcon /></ModalCloseButton>
      <ModalBody>
        {loadError ? <AdminState status="error" title="Choices could not be loaded"
          description="Pinpoint could not load the credential and tool catalogs. Try again."
          onRetry={() => setRevision(value => value + 1)} /> : !choices ?
          <AdminState status="loading" title="Loading credentials and tools…" /> :
          <VStack spacing={5} align="stretch">
            <Text fontSize="sm" color="gray.600">Choose the credential that grants access to this tool. This requirement applies across spaces that use badge integration.</Text>
            <FormControl isRequired isDisabled={pending}>
              <FormLabel id="requirement-credential-label" htmlFor="requirement-credential">Credential</FormLabel>
              <Autocomplete id="requirement-credential" onOpen={() => setCredentialOpen(true)} onClose={() => setCredentialOpen(false)}
                aria-controls={credentialOpen ? "requirement-credential-popup" : undefined} options={choices.credentials} value={credential}
                disabled={pending} onChange={(_, value) => setCredential(value)}
                getOptionLabel={option => option.name} getOptionSelected={(a, b) => a.id === b.id && a.provider === b.provider}
                filterOptions={credentialFilter} noOptionsText="No matching credentials" PopperComponent={ChoicePopper}
                popupIcon={<ChevronDownIcon />} closeIcon={<CloseIcon />} ListboxProps={{ style: { maxHeight: 200 } }}
                renderOption={option => <Box minW={0} py={1}>
                  <Text fontSize="sm" fontWeight="medium" overflowWrap="anywhere">{option.name}</Text>
                  <Text fontSize="xs" color="gray.600" overflowWrap="anywhere">{option.id}</Text>
                </Box>}
                renderInput={params => <TextField {...params} inputRef={credentialRef} variant="outlined"
                  placeholder="Search credentials by name or ID" inputProps={{ ...params.inputProps, 'aria-required': true }} />} />
              {credential && <Text fontSize="xs" mt={2} color="gray.600" overflowWrap="anywhere">Credential ID: {credential.id}</Text>}
            </FormControl>
            <FormControl isRequired isDisabled={pending}>
              <FormLabel id="requirement-tool-label" htmlFor="requirement-tool">Tool</FormLabel>
              <Autocomplete id="requirement-tool" onOpen={() => setToolOpen(true)} onClose={() => setToolOpen(false)}
                aria-controls={toolOpen ? "requirement-tool-popup" : undefined} options={choices.tools} value={tool} disabled={pending}
                onChange={(_, value) => setTool(value)} getOptionLabel={option => option.name}
                getOptionSelected={(a, b) => a.id === b.id} noOptionsText="No matching tools" PopperComponent={ChoicePopper}
                popupIcon={<ChevronDownIcon />} closeIcon={<CloseIcon />} ListboxProps={{ style: { maxHeight: 200 } }}
                renderOption={option => <Text fontSize="sm" overflowWrap="anywhere">{option.name}</Text>}
                renderInput={params => <TextField {...params} variant="outlined" placeholder="Search tools"
                  inputProps={{ ...params.inputProps, 'aria-required': true }} />} />
            </FormControl>
            {(!choices.credentials.length || !choices.tools.length) && <AdminState
              title={!choices.credentials.length ? 'No credentials available' : 'No tools available'}
              description={!choices.credentials.length ? 'Your credential provider has no available credentials. Create a credential with the provider before linking it here.' :
                'Create a tool before adding a badge requirement.'} />}
          </VStack>}
        {saveError && <AdminState mt={4} status="error" title="Badge requirement could not be added" description={saveError} />}
      </ModalBody>
      <ModalFooter gridGap={2} flexWrap="wrap">
        <Button variant="outline" onClick={close} isDisabled={pending}>Cancel</Button>
        <Button type="submit" colorScheme="blue" isDisabled={!credential || !tool || !choices || pending}
          isLoading={pending} loadingText="Adding…">Add requirement</Button>
      </ModalFooter>
    </ModalContent>
  </Modal>;
}
